import React, { useState } from "react";
import { Link } from "@/i18n/router";
import { toast } from "sonner";
import { Linkedin, Mail, MapPin, ArrowUpRight, Send } from "lucide-react";
import { Logo } from "@/components/Logo";
import { footerLinks } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const { tr } = useLang();
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
      setEmail("");
      toast.success("Subscribed. Thank you for joining the AstroLife briefing.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not subscribe.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border-b border-navy-border">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-5 py-12 lg:flex-row lg:items-center lg:justify-between lg:px-8">
        <div>
          <h3 className="font-heading text-xl font-bold text-white">{tr("The AstroLife Briefing")}</h3>
          <p className="mt-1 text-sm text-silver/60">{tr("Governance insights, AI Act updates and research — occasionally, never spam.")}</p>
        </div>
        <form onSubmit={submit} className="flex w-full max-w-md gap-2" data-testid="newsletter-form">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@organisation.eu"
            data-testid="newsletter-email"
            className="flex-1 rounded-full border border-navy-border bg-navy-surface px-5 py-3 text-sm text-white placeholder:text-silver/40 focus:border-astro-blue focus:outline-none"
          />
          <button type="submit" disabled={busy} data-testid="newsletter-submit" className="inline-flex items-center gap-2 rounded-full bg-astro-blue px-5 py-3 text-sm font-semibold text-white hover:bg-astro-light transition-colors disabled:opacity-60">
            {busy ? "…" : tr("Subscribe")} <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
}

export function Footer() {
  const { tr } = useLang();
  return (
    <footer className="border-t border-navy-border bg-navy" data-testid="main-footer">
      <NewsletterForm />
      <div className="mx-auto max-w-7xl px-5 lg:px-8 py-16">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-3 lg:grid-cols-6">
          <div className="col-span-2">
            <Logo />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-silver/60">
              {tr("Sovereign Artificial Intelligence Infrastructure and Verifiable AI Governance for governments, critical infrastructure and regulated industries across Europe.")}
            </p>
            <div className="mt-6 space-y-2 text-sm text-silver/60">
              <a href="mailto:synthia@astrolife.es" className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail className="h-4 w-4 text-astro-light" /> synthia@astrolife.es
              </a>
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-astro-light" /> {tr("European Union")}
              </span>
              <a
                href="https://www.linkedin.com/company/astrolife"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-white transition-colors"
                data-testid="footer-linkedin"
              >
                <Linkedin className="h-4 w-4 text-astro-light" /> LinkedIn
              </a>
            </div>
          </div>

          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-astro-light">{tr(title)}</h3>
              <ul className="mt-4 space-y-2.5">
                {links.map((l) => (
                  <li key={l.label}>
                    {l.external ? (
                      <a
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-silver/70 hover:text-white transition-colors"
                      >
                        {tr(l.label)} <ArrowUpRight className="h-3 w-3" />
                      </a>
                    ) : (
                      <Link to={l.to} className="text-sm text-silver/70 hover:text-white transition-colors">
                        {tr(l.label)}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-navy-border pt-8 md:flex-row md:items-center">
          <p className="text-xs text-silver/50">
            © {new Date().getFullYear()} AstroLife™. {tr("Sovereign AI Infrastructure. All rights reserved.")}
          </p>
          <div className="flex gap-5 text-xs text-silver/50">
            <Link to="/contact" className="hover:text-white transition-colors">{tr("Contact")}</Link>
            <Link to="/private-beta" className="hover:text-white transition-colors">{tr("Private Beta")}</Link>
            <span>{tr("Aligned with EU digital sovereignty frameworks")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
